import { type ReactNode, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { CivilGridFile, CivilSearchMessage } from '~/common/types';
import Message from '~/components2/Messages/Message';
import MessageContainer from '~/components2/Messages/MessageContainer';
import { useScrollTo } from '~/hooks/useScrollTo';
import { downloadAndTrackUtilitiesFile } from '../../common/trackUtilitiesFileDownload';
import store from '../../store';

interface Props {
  messages: CivilSearchMessage[];
}

export default function MessageList({ messages }: Props) {
  const { project } = useSnapshot(store) as typeof store;
  const { ref: scrollRef } = useScrollTo([messages]);

  const onFileDownload = async (file: CivilGridFile) => {
    if (project) {
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchFileClicked, {
        project_id: project.id,
        file_id: file.id,
        file_name: file.name,
      });
    }

    await downloadAndTrackUtilitiesFile(file, 'message', project?.id);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const messageItems = useMemo(() => {
    return messages.map((m) => (
      <Message
        key={`message-item-${m.id}`}
        createdBy={m.createdBy}
        createdAt={m.createdAt}
        files={m.files}
        onDownload={onFileDownload}
      >
        {m.message}
      </Message>
    ));
  }, [messages]);

  let content: ReactNode = (
    <div className="h-full pt-48 text-center text-sm">
      Nothing here yet! Use the Project Channel to reach out to CivilGrid regarding any questions about your data
      requests.
      <br />
      <br />💡 Tip: You can also send files!
    </div>
  );

  if (messageItems.length > 0) {
    content = messageItems;
  }

  return (
    <MessageContainer>
      {content}
      <div ref={scrollRef} />
    </MessageContainer>
  );
}
